<template>
  <div class="categories-container">
    <!-- Header -->
    <div class="page-header">
      <h1>Gestion des Catégories</h1>
      <button
        class="btn btn-primary"
        @click="showCreateModal = true"
      >
        <i class="fas fa-plus" /> Nouvelle Catégorie
      </button>
    </div>

    <!-- Categories Grid -->
    <div
      v-if="categories.length > 0"
      class="categories-grid"
    >
      <div
        v-for="category in categories"
        :key="category.id"
        class="category-card"
        :style="{ borderLeftColor: category.color }"
      >
        <div class="category-header">
          <div
            class="category-color"
            :style="{ backgroundColor: category.color }"
          />
          <div class="category-info">
            <h3>{{ category.name }}</h3>
            <p v-if="category.description">
              {{ category.description }}
            </p>
          </div>
        </div>

        <div class="category-stats">
          <div class="stat-item">
            <i class="fas fa-file-invoice" />
            <span>{{ category.expense_count || 0 }} dépense(s)</span>
          </div>
        </div>

        <div class="category-actions">
          <button
            class="btn-action btn-edit"
            title="Modifier"
            @click="editCategory(category)"
          >
            Modifier
          </button>
          <button
            class="btn-action btn-delete"
            title="Supprimer"
            @click="deleteCategory(category)"
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>

    <div
      v-else-if="loading"
      class="loading-state"
    >
      <i class="fas fa-spinner fa-spin" /> Chargement...
    </div>

    <div
      v-else
      class="empty-state"
    >
      <i class="fas fa-tags" />
      <p>Aucune catégorie trouvée</p>
      <button
        class="btn btn-primary"
        @click="showCreateModal = true"
      >
        <i class="fas fa-plus" /> Créer la première catégorie
      </button>
    </div>

    <!-- Create/Edit Modal -->
    <div
      v-if="showCreateModal || showEditModal"
      class="modal-overlay"
      @click.self="closeModal"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ showEditModal ? 'Modifier la Catégorie' : 'Nouvelle Catégorie' }}</h2>
          <button
            class="btn-close"
            @click="closeModal"
          >
            <i class="fas fa-times" />
          </button>
        </div>

        <form
          class="modal-form"
          @submit.prevent="saveCategory"
        >
          <div class="form-group">
            <label class="required">Nom</label>
            <input
              v-model="form.name"
              type="text"
              required
            >
          </div>

          <div class="form-group">
            <label class="required">Clé (ID unique)</label>
            <input
              v-model.number="form.key"
              type="number"
              required
            >
            <small class="help-text">Identifiant numérique unique pour cette catégorie</small>
          </div>

          <div class="form-group">
            <label>Description</label>
            <textarea
              v-model="form.description"
              rows="3"
            />
          </div>

          <div class="form-group">
            <label class="required">Couleur</label>
            <div class="color-picker-group">
              <input
                v-model="form.color"
                type="color"
                required
              >
              <input
                v-model="form.color"
                type="text"
                pattern="^#[0-9A-Fa-f]{6}$"
                placeholder="#6366f1"
              >
              <div
                class="color-preview"
                :style="{ backgroundColor: form.color }"
              />
            </div>
            <small class="help-text">Couleur d'affichage dans l'interface</small>
          </div>

          <div class="modal-actions">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeModal"
            >
              <i class="fas fa-times" /> Annuler
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="btn btn-primary"
            >
              <i
                class="fas"
                :class="saving ? 'fa-spinner fa-spin' : 'fa-save'"
              />
              {{ saving ? 'Enregistrement...' : 'Enregistrer' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CategoriesIndex',
  data() {
    return {
      categories: [],
      loading: false,
      saving: false,
      showCreateModal: false,
      showEditModal: false,
      editingCategory: null,
      form: {
        name: '',
        key: '',
        description: '',
        color: '#6366f1',
      },
    }
  },
  mounted() {
    this.loadCategories()
  },
  methods: {
    async loadCategories() {
      this.loading = true
      try {
        const response = await axios.get('/api/categories/')
        this.categories = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading categories:', error)
        this.$toast.error('Erreur lors du chargement des catégories')
      } finally {
        this.loading = false
      }
    },

    editCategory(category) {
      this.editingCategory = category
      this.form = {
        name: category.name,
        key: category.key,
        description: category.description || '',
        color: category.color || '#6366f1',
      }
      this.showEditModal = true
    },

    async saveCategory() {
      this.saving = true
      try {
        if (this.showEditModal) {
          await axios.put(`/api/categories/update/${this.editingCategory.id}/`, this.form)
          this.$toast.success('Catégorie modifiée avec succès')
        } else {
          await axios.post('/api/categories/create/', this.form)
          this.$toast.success('Catégorie créée avec succès')
        }
        this.closeModal()
        this.loadCategories()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving category:', error)
        this.$toast.error('Erreur lors de l\'enregistrement')
      } finally {
        this.saving = false
      }
    },

    async deleteCategory(category) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Supprimer la catégorie "${category.name}" ?`)) {
        return
      }

      try {
        await axios.delete(`/api/categories/update/${category.id}/`)
        this.$toast.success('Catégorie supprimée avec succès')
        this.loadCategories()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting category:', error)

        // Check if it's a protected error (category has related expenses)
        if (error.response && error.response.status === 500) {
          const errorMessage = error.response.data?.detail || error.response.data?.error || ''
          if (errorMessage.includes('protected') || errorMessage.includes('referenced')) {
            this.$toast.error(`Impossible de supprimer la catégorie "${category.name}" car elle est utilisée par ${category.expense_count} dépense(s). Veuillez d'abord supprimer ou réassigner ces dépenses.`)
          } else {
            this.$toast.error('Erreur lors de la suppression de la catégorie')
          }
        } else {
          this.$toast.error('Erreur lors de la suppression')
        }
      }
    },

    closeModal() {
      this.showCreateModal = false
      this.showEditModal = false
      this.editingCategory = null
      this.form = {
        name: '',
        key: '',
        description: '',
        color: '#6366f1',
      }
    },
  },
}
</script>

<style scoped>
.categories-container {
  padding: 20px;
  max-width: 100%;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.page-header h1 {
  font-size: 28px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.category-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 4px solid;
  transition: transform 0.2s, box-shadow 0.2s;
}

.category-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.category-header {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.category-color {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  flex-shrink: 0;
}

.category-info {
  flex: 1;
}

.category-info h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 5px 0;
}

.category-info p {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.category-stats {
  padding: 15px 0;
  border-top: 1px solid #f3f4f6;
  border-bottom: 1px solid #f3f4f6;
  margin-bottom: 15px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #4b5563;
}

.stat-item i {
  color: #9ca3af;
}

.category-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.btn-action {
  padding: 6px 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.btn-action.btn-edit {
  background: #fef3c7;
  color: #92400e;
}

.btn-action.btn-edit:hover {
  background: #fde68a;
  color: #78350f;
}

.btn-action.btn-delete {
  background: #fee2e2;
  color: #dc2626;
}

.btn-action.btn-delete:hover {
  background: #fecaca;
  color: #991b1b;
}

.loading-state,
.empty-state {
  padding: 60px 20px;
  text-align: center;
  color: #6b7280;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #d1d5db;
}

.empty-state p {
  margin-bottom: 20px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.btn-close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #e5e7eb;
  color: #1f2937;
}

.modal-form {
  padding: 25px;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.form-group label.required::after {
  content: ' *';
  color: #ef4444;
}

.form-group input,
.form-group textarea {
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.help-text {
  font-size: 12px;
  color: #6b7280;
  margin-top: 5px;
}

.color-picker-group {
  display: grid;
  grid-template-columns: 60px 1fr 40px;
  gap: 10px;
  align-items: center;
}

.color-picker-group input[type="color"] {
  height: 40px;
  padding: 2px;
  cursor: pointer;
}

.color-preview {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-secondary {
  background: #f3f4f6;
  color: #4b5563;
}

.btn-secondary:hover {
  background: #e5e7eb;
}
</style>
